import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ChevronDown, LogIn, Plus, Save, Trash2, UserRound, X } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { ApiError } from "../../../api/client";
import {
  createComparisonPreset,
  deleteComparisonPreset,
  getComparisonPresets,
  updateComparisonPreset,
} from "../../account/api";
import type {
  ComparisonPreset,
  ComparisonPresetListResponse,
} from "../../account/types";
import { useAccount } from "../../account/useAccount";
import {
  comparisonPresetsEqual,
  normalizeComparisonPreset,
  serializeComparisonPreset,
} from "../presets";
import type { ComparisonFilters } from "../types";

const PRESETS_QUERY_KEY = ["account", "comparison-presets"] as const;
const EMPTY_PRESETS: ComparisonPreset[] = [];

interface FilterProfilesProps {
  filters: ComparisonFilters;
  onChange: (filters: ComparisonFilters) => void;
  onRequestAccount: () => void;
}

function mutationError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.message.includes("already exists")) {
      return "Профиль с таким названием уже существует.";
    }
    return error.message;
  }
  return "Не удалось сохранить профиль.";
}

function sortPresets(rows: ComparisonPreset[]): ComparisonPreset[] {
  return [...rows].sort((left, right) =>
    left.name.localeCompare(right.name, "ru"),
  );
}

export function FilterProfiles({
  filters,
  onChange,
  onRequestAccount,
}: FilterProfilesProps) {
  const queryClient = useQueryClient();
  const { account, isAuthenticated, isLoading: isAccountLoading } =
    useAccount();
  const ownerId = account?.user.public_id ?? null;
  const [selected, setSelected] = useState<{
    ownerId: string;
    presetId: number;
  } | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [profileName, setProfileName] = useState("");

  const presetsQuery = useQuery({
    queryKey: PRESETS_QUERY_KEY,
    queryFn: ({ signal }) => getComparisonPresets(signal),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const presets = presetsQuery.data?.rows ?? EMPTY_PRESETS;
  const selectedPreset = useMemo(
    () =>
      selected?.ownerId === ownerId
        ? presets.find((preset) => preset.id === selected.presetId) ?? null
        : null,
    [ownerId, presets, selected],
  );
  const hasChanges =
    selectedPreset !== null &&
    !comparisonPresetsEqual(
      filters,
      normalizeComparisonPreset(selectedPreset.filters),
    );

  const createMutation = useMutation({
    mutationFn: createComparisonPreset,
    onSuccess: ({ preset }) => {
      queryClient.setQueryData<ComparisonPresetListResponse>(
        PRESETS_QUERY_KEY,
        (current) => ({
          ok: true,
          rows: sortPresets([...(current?.rows ?? []), preset]),
        }),
      );
      if (ownerId) {
        setSelected({ ownerId, presetId: preset.id });
      }
      setProfileName("");
      setIsCreating(false);
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({
      presetId,
      preset,
    }: {
      presetId: number;
      preset: ComparisonPreset;
    }) =>
      updateComparisonPreset(presetId, {
        name: preset.name,
        filters: serializeComparisonPreset(filters),
        is_default: preset.is_default,
      }),
    onSuccess: ({ preset }) => {
      queryClient.setQueryData<ComparisonPresetListResponse>(
        PRESETS_QUERY_KEY,
        (current) => ({
          ok: true,
          rows: sortPresets(
            (current?.rows ?? []).map((row) =>
              row.id === preset.id ? preset : row,
            ),
          ),
        }),
      );
    },
  });
  const deleteMutation = useMutation({
    mutationFn: deleteComparisonPreset,
    onSuccess: (_response, presetId) => {
      queryClient.setQueryData<ComparisonPresetListResponse>(
        PRESETS_QUERY_KEY,
        (current) => ({
          ok: true,
          rows: (current?.rows ?? []).filter((row) => row.id !== presetId),
        }),
      );
      setSelected(null);
    },
  });

  const submitProfile = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = profileName.trim();
    if (!name || createMutation.isPending) {
      return;
    }
    createMutation.mutate({
      name,
      filters: serializeComparisonPreset(filters),
      is_default: false,
    });
  };
  const error =
    createMutation.error ??
    updateMutation.error ??
    deleteMutation.error ??
    presetsQuery.error;

  return (
    <section className="filter-profiles" aria-labelledby="filter-profiles-title">
      <header>
        <span className="filter-profile-icon" aria-hidden="true">
          <UserRound />
        </span>
        <div>
          <h2 id="filter-profiles-title">Профили</h2>
          <p>Личные наборы параметров</p>
        </div>
        {isAuthenticated && presets.length > 0 ? (
          <span className="filter-profile-count">{presets.length}</span>
        ) : null}
      </header>

      {isAccountLoading ? (
        <div className="filter-profile-placeholder">Проверяем профиль...</div>
      ) : !isAuthenticated ? (
        <div className="filter-profile-guest">
          <span>Войдите, чтобы сохранять настройки.</span>
          <button type="button" onClick={onRequestAccount}>
            <LogIn aria-hidden="true" />
            Войти
          </button>
        </div>
      ) : (
        <>
          <div className="filter-profile-toolbar">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="filter-profile-select" type="button">
                  <span>
                    {selectedPreset?.name ??
                      (presetsQuery.isPending
                        ? "Загрузка..."
                        : "Выбрать профиль")}
                  </span>
                  <ChevronDown aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="market-select-content filter-profile-menu"
                  align="end"
                  sideOffset={6}
                >
                  {presets.length === 0 ? (
                    <div className="filter-profile-empty">
                      Сохранённых профилей пока нет
                    </div>
                  ) : (
                    presets.map((preset) => (
                      <DropdownMenu.Item
                        className="market-select-item filter-profile-menu-item"
                        key={preset.id}
                        onSelect={() => {
                          if (ownerId) {
                            setSelected({
                              ownerId,
                              presetId: preset.id,
                            });
                          }
                          onChange(normalizeComparisonPreset(preset.filters));
                        }}
                      >
                        <span>{preset.name}</span>
                        {preset.id === selectedPreset?.id ? (
                          <span className="filter-profile-selected-dot" />
                        ) : null}
                      </DropdownMenu.Item>
                    ))
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <button
              className="filter-profile-icon-button"
              type="button"
              title="Создать профиль"
              aria-label="Создать профиль"
              onClick={() => {
                createMutation.reset();
                setIsCreating(true);
              }}
            >
              <Plus aria-hidden="true" />
            </button>
          </div>

          {isCreating ? (
            <form className="filter-profile-create" onSubmit={submitProfile}>
              <input
                autoFocus
                maxLength={80}
                value={profileName}
                placeholder="Название профиля"
                aria-label="Название нового профиля"
                onChange={(event) => setProfileName(event.currentTarget.value)}
              />
              <button
                type="submit"
                title="Сохранить новый профиль"
                aria-label="Сохранить новый профиль"
                disabled={!profileName.trim() || createMutation.isPending}
              >
                <Save aria-hidden="true" />
              </button>
              <button
                type="button"
                title="Отменить создание"
                aria-label="Отменить создание"
                onClick={() => {
                  setIsCreating(false);
                  setProfileName("");
                  createMutation.reset();
                }}
              >
                <X aria-hidden="true" />
              </button>
            </form>
          ) : selectedPreset ? (
            <div className="filter-profile-actions">
              <span className={hasChanges ? "is-dirty" : ""}>
                {hasChanges ? "Есть изменения" : "Настройки сохранены"}
              </span>
              <button
                type="button"
                disabled={!hasChanges || updateMutation.isPending}
                title="Перезаписать профиль"
                aria-label="Перезаписать профиль текущими настройками"
                onClick={() =>
                  updateMutation.mutate({
                    presetId: selectedPreset.id,
                    preset: selectedPreset,
                  })
                }
              >
                <Save aria-hidden="true" />
              </button>
              <button
                className="is-danger"
                type="button"
                disabled={deleteMutation.isPending}
                title="Удалить профиль"
                aria-label="Удалить профиль"
                onClick={() => deleteMutation.mutate(selectedPreset.id)}
              >
                <Trash2 aria-hidden="true" />
              </button>
            </div>
          ) : (
            <p className="filter-profile-hint">
              Сохраните текущие фильтры под своим названием.
            </p>
          )}

          {error ? (
            <p className="filter-profile-error" role="alert">
              {mutationError(error)}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
